import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import "@/App.css";

import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import HostelDetail from "@/pages/HostelDetail";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyBooking from "@/pages/VerifyBooking";
import StudentDashboard from "@/pages/StudentDashboard";
import KYC from "@/pages/KYC";
import Booking from "@/pages/Booking";
import OwnerDashboard from "@/pages/OwnerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CityPage from "@/pages/CityPage";
import { CITY_SLUGS } from "@/lib/cityMeta";
import InstallPrompt from "@/components/InstallPrompt";
import MobileGetStartedBar from "@/components/MobileGetStartedBar";
import KycFloatingBanner from "@/components/KycFloatingBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import CookieBanner from "@/components/CookieBanner";

const STUDENT_OR_ADMIN = ["student", "admin"];
const STUDENT_ONLY = ["student"];
const OWNER_OR_ADMIN = ["owner", "admin"];
const ADMIN_ONLY = ["admin"];

function Guard({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-20 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <ImpersonationBanner />
        <InstallPrompt />
        <MobileGetStartedBar />
        <KycFloatingBanner />
        <CookieBanner />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/hostels" element={<Browse />} />
          <Route path="/hostels/:id" element={<HostelDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify" element={<VerifyBooking />} />
          <Route path="/dashboard" element={<Guard roles={STUDENT_OR_ADMIN}><StudentDashboard /></Guard>} />
          <Route path="/kyc" element={<Guard roles={STUDENT_ONLY}><KYC /></Guard>} />
          <Route path="/booking/:id" element={<Guard roles={STUDENT_OR_ADMIN}><Booking /></Guard>} />
          <Route path="/owner" element={<Guard roles={OWNER_OR_ADMIN}><OwnerDashboard /></Guard>} />
          <Route path="/admin" element={<Guard roles={ADMIN_ONLY}><AdminDashboard /></Guard>} />
          {CITY_SLUGS.map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<CityPage />} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
